/** Auth0 audience is required for API bearer tokens in deployed environments. */
export function authAudienceConfigured() {
  return Boolean(process.env.REACT_APP_AUTH0_AUDIENCE);
}

const BASE_SCOPES = 'openid profile email';
// offline_access is required when Auth0Provider uses useRefreshTokens — without it,
// getAccessTokenSilently fails after login (missing_refresh_token) and AuthSessionGuard
// force-reauths in a loop once ENFORCE_API_AUTH is on.
const PORTAL_API_SCOPES = 'read:contract-state write:contract-state offline_access';
export const PORTAL_READ_SCOPE = 'read:contract-state';
export const PORTAL_WRITE_SCOPE = 'write:contract-state';

/** Scopes for Auth0 login and silent token refresh when the portal API audience is set. */
export function portalAuthScope() {
  if (!authAudienceConfigured()) {
    return BASE_SCOPES;
  }
  return `${BASE_SCOPES} ${PORTAL_API_SCOPES}`;
}

/** Merge portal audience + API scopes into login redirect params. */
export function portalLoginAuthorizationParams(overrides = {}) {
  return {
    ...(authAudienceConfigured() && {
      audience: process.env.REACT_APP_AUTH0_AUDIENCE,
    }),
    scope: portalAuthScope(),
    ...overrides,
  };
}

const TOKEN_EXPIRY_SKEW_MS = 30 * 1000;

/** Decode a JWT payload without verifying the signature (client-side scope inspection only). */
export function decodeJwtPayload(token) {
  if (typeof token !== 'string' || token.split('.').length !== 3) {
    return null;
  }
  try {
    const payload = token.split('.')[1];
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const normalized = padded.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(normalized));
  } catch (_err) {
    return null;
  }
}

/** True when a JWT is present and not inside the expiry skew window. */
export function jwtIsFresh(token, nowMs = Date.now()) {
  const payload = decodeJwtPayload(token);
  const exp = Number(payload?.exp);
  if (!Number.isFinite(exp) || exp <= 0) {
    return false;
  }
  return exp * 1000 > nowMs + TOKEN_EXPIRY_SKEW_MS;
}

/** True when the access token includes portal data-route scopes (scope or permissions claim). */
export function portalScopesGrantedInToken(token) {
  if (!authAudienceConfigured()) {
    return true;
  }
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return false;
  }
  const granted = new Set(
    String(payload.scope || '')
      .split(/\s+/)
      .filter(Boolean),
  );
  for (const permission of payload.permissions || []) {
    if (permission) {
      granted.add(String(permission));
    }
  }
  return granted.has(PORTAL_READ_SCOPE);
}

/** Short diagnostic for the portal-scope blocked screen. Never includes the token. */
export function describePortalAccessFailure({ token, error } = {}) {
  if (error) {
    const code = error.error || error.code || error.message || 'unknown';
    return `Auth0 did not return a portal API token (${code}).`;
  }
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return 'Auth0 returned a token that is not a portal API JWT.';
  }
  const audience = Array.isArray(payload.aud) ? payload.aud.join(', ') : (payload.aud || 'none');
  const scope = payload.scope || '(none)';
  const permissions = (payload.permissions || []).join(' ') || '(none)';
  return `Token audience ${audience}; scope ${scope}; permissions ${permissions}.`;
}

/** Options for getAccessTokenSilently so bearer tokens include portal API scopes. */
export function portalApiTokenOptions({ cacheMode } = {}) {
  const extra = cacheMode ? { cacheMode } : {};
  if (!authAudienceConfigured()) {
    return extra;
  }
  return {
    ...extra,
    authorizationParams: {
      // Audience must be explicit: scope-only overrides have historically dropped the
      // provider audience in some Auth0 SPA SDK paths, yielding opaque/unusable tokens.
      audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      scope: portalAuthScope(),
    },
  };
}

const RECOVERABLE_AUTH_ERRORS = new Set([
  'login_required',
  'consent_required',
  'missing_refresh_token',
  'invalid_grant',
  'interaction_required',
  'access_denied',
]);

/** True when Auth0 indicates the user must sign in again. */
export function isRecoverableAuthError(err) {
  const code = (err?.error || err?.code || '').toLowerCase();
  return RECOVERABLE_AUTH_ERRORS.has(code);
}

let reauthInProgress = false;

export const PORTAL_SCOPE_RECOVERY_KEY = 'keyflo_portal_scope_recovery_attempted';
export const PORTAL_SCOPE_LOGIN_RECOVERY_KEY = 'keyflo_portal_scope_login_recovery_attempted';
export const FRESH_AUTH_REDIRECT_KEY = 'keyflo_fresh_auth_redirect';

export function markFreshAuthRedirect() {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.setItem(FRESH_AUTH_REDIRECT_KEY, '1');
}

export function isFreshAuthRedirect() {
  if (typeof window === 'undefined') {
    return false;
  }
  return sessionStorage.getItem(FRESH_AUTH_REDIRECT_KEY) === '1';
}

export function clearFreshAuthRedirect() {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.removeItem(FRESH_AUTH_REDIRECT_KEY);
}

/** Peek-and-clear. Prefer isFreshAuthRedirect during session validate so remounts keep the flag. */
export function consumeFreshAuthRedirect() {
  const fresh = isFreshAuthRedirect();
  if (fresh) {
    clearFreshAuthRedirect();
  }
  return fresh;
}

export function hasAttemptedPortalScopeRecovery() {
  if (typeof window === 'undefined') {
    return false;
  }
  return sessionStorage.getItem(PORTAL_SCOPE_RECOVERY_KEY) === '1';
}

export function markPortalScopeRecoveryAttempted() {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.setItem(PORTAL_SCOPE_RECOVERY_KEY, '1');
}

export function clearPortalScopeRecoveryAttempted() {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.removeItem(PORTAL_SCOPE_RECOVERY_KEY);
}

export function hasAttemptedPortalScopeLoginRecovery() {
  if (typeof window === 'undefined') {
    return false;
  }
  return sessionStorage.getItem(PORTAL_SCOPE_LOGIN_RECOVERY_KEY) === '1';
}

export function markPortalScopeLoginRecoveryAttempted() {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.setItem(PORTAL_SCOPE_LOGIN_RECOVERY_KEY, '1');
}

export function clearPortalScopeLoginRecoveryAttempted() {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.removeItem(PORTAL_SCOPE_LOGIN_RECOVERY_KEY);
}

export function clearPortalScopeRecoveryState() {
  clearPortalScopeRecoveryAttempted();
  clearPortalScopeLoginRecoveryAttempted();
  clearPortalSilentAuthRecoveryAttempted();
}

export function isReauthenticationInProgress() {
  return reauthInProgress;
}

export function markReauthenticationStarted() {
  reauthInProgress = true;
}

export function resetReauthenticationState() {
  reauthInProgress = false;
}

/** Request API scopes via Auth0 redirect without clearing the browser session first. */
export async function requestPortalScopeAuthorization({
  loginWithRedirect,
  prompt = 'consent',
} = {}) {
  if (reauthInProgress) {
    return false;
  }
  markReauthenticationStarted();

  const returnTo = `${window.location.pathname}${window.location.search}`;

  try {
    await loginWithRedirect({
      appState: { returnTo },
      authorizationParams: portalLoginAuthorizationParams({ prompt }),
    });
    return true;
  } catch (err) {
    resetReauthenticationState();
    console.warn('Auth session: scope authorization redirect failed', err);
    throw err;
  }
}

/** Clear cached Auth0 session and send the user through login again. */
export async function forceReauthentication({
  logout,
  loginWithRedirect,
  prompt = 'login',
} = {}) {
  if (reauthInProgress) {
    return false;
  }
  markReauthenticationStarted();

  const returnTo = `${window.location.pathname}${window.location.search}`;

  try {
    try {
      await logout({ openUrl: false });
    } catch (err) {
      console.warn('Auth session: logout before re-login failed', err);
    }

    await loginWithRedirect({
      appState: { returnTo },
      authorizationParams: portalLoginAuthorizationParams({ prompt }),
    });
    return true;
  } catch (err) {
    resetReauthenticationState();
    console.warn('Auth session: login redirect failed', err);
    throw err;
  }
}

export const PENDING_SIGNUP_PATH_KEY = 'keyflo_pending_signup_path';

/** Auth0 Allowed Logout URLs are origin-only; stash the post-logout signup path separately. */
export function stashPendingSignupPath(path) {
  if (typeof window === 'undefined' || !path) {
    return;
  }
  window.sessionStorage.setItem(PENDING_SIGNUP_PATH_KEY, path);
}

export function consumePendingSignupPath() {
  if (typeof window === 'undefined') {
    return null;
  }
  const path = window.sessionStorage.getItem(PENDING_SIGNUP_PATH_KEY);
  if (path) {
    window.sessionStorage.removeItem(PENDING_SIGNUP_PATH_KEY);
  }
  return path;
}

export function logoutForSignupSwitch(logout, signupPath) {
  stashPendingSignupPath(signupPath);
  logout({ logoutParams: { returnTo: window.location.origin } });
}

export const PORTAL_SILENT_AUTH_RECOVERY_KEY = 'keyflo_portal_silent_auth_recovery_attempted';

export function hasAttemptedPortalSilentAuthRecovery() {
  if (typeof window === 'undefined') {
    return false;
  }
  return sessionStorage.getItem(PORTAL_SILENT_AUTH_RECOVERY_KEY) === '1';
}

export function markPortalSilentAuthRecoveryAttempted() {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.setItem(PORTAL_SILENT_AUTH_RECOVERY_KEY, '1');
}

export function clearPortalSilentAuthRecoveryAttempted() {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.removeItem(PORTAL_SILENT_AUTH_RECOVERY_KEY);
}
